import React from 'react';
import * as Sentry from "@sentry/react";

/**
 * Handles server errors by logging them to Sentry and redirecting to the 500 error page
 * 
 * @param error The error object
 * @param context Optional context information about where the error occurred
 */
export const handleServerError = (error: unknown, context?: Record<string, any>): void => {
  console.error('Server error:', error);
  
  // Log to Sentry with additional context if available
  if (context) {
    Sentry.captureException(error, {
      extra: context
    });
  } else {
    Sentry.captureException(error);
  }
  
  // Redirect to the 500 error page
  window.location.href = '/500';
};

/**
 * Error boundary component that catches errors in React components
 * and redirects to the 500 error page
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    handleServerError(error, {
      componentStack: errorInfo.componentStack
    });
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI or redirect
      window.location.href = '/500';
      return null;
    }

    return this.props.children;
  }
}
